import type { MoveRingAction } from "../types/action";
import type { PlayerColor } from "../types/common";
import type { Board, YinshState } from "../types/game-state";
import { flipMarkers, getMarkersJumped } from "../utils/marker-utils";
import { detectRows } from "../utils/row-detection";

function getOpponent(player: PlayerColor): PlayerColor {
  return player === "WHITE" ? "BLACK" : "WHITE";
}

function resolveMarkerExhaustion(state: YinshState): YinshState {
  const [p0, p1] = state.players;
  if (p0.ringsScored > p1.ringsScored) {
    return { ...state, phase: "GAME_END", winner: p0.color };
  }
  if (p1.ringsScored > p0.ringsScored) {
    return { ...state, phase: "GAME_END", winner: p1.color };
  }
  return { ...state, phase: "GAME_END", winner: null, isDraw: true };
}

export function applyMoveRing(
  state: YinshState,
  action: MoveRingAction,
): YinshState {
  // Compute jumped markers from original board before placing the ring
  const jumped = getMarkersJumped(state.board, action.from, action.to);
  const boardAfterFlip = flipMarkers(state.board, jumped);

  // Place ring at destination on the already-flipped board
  const boardWithRing: Board = {
    ...boardAfterFlip,
    [action.to]: { type: "RING", player: action.player },
  };

  const currentRows = detectRows(boardWithRing, action.player);
  const opponentRows = detectRows(boardWithRing, getOpponent(action.player));

  const baseState: YinshState = {
    ...state,
    board: boardWithRing,
    markerPlacedAt: null,
    // Record this move for the client's last-action highlight. `jumped` is
    // exactly the set of markers this move flipped. Inherited by every branch
    // below (own-row, opponent-row, exhaustion, normal turn-pass).
    lastMove: {
      player: action.player,
      from: action.from,
      to: action.to,
      flipped: jumped,
    },
  };

  if (currentRows.length > 0) {
    // Mover formed at least one own 5-row — they resolve first, opponent's
    // rows (if any) are queued after.
    return {
      ...baseState,
      phase: "REMOVING_ROW",
      currentPlayer: action.player,
      pendingRows: currentRows,
      opponentPendingRows: opponentRows,
      rowsRemovedThisTurn: 0,
      lastMover: action.player,
    };
  }

  if (opponentRows.length > 0) {
    // Mover formed only opponent 5-row(s) (e.g. by flipping markers via
    // jump). The opponent — not the mover — selects markers and removes a
    // ring. Hand control directly to the opponent. lastMover is preserved
    // so the final turn-pass lands on the opponent of the original mover.
    return {
      ...baseState,
      phase: "REMOVING_ROW",
      currentPlayer: getOpponent(action.player),
      pendingRows: opponentRows,
      opponentPendingRows: [],
      rowsRemovedThisTurn: 0,
      lastMover: action.player,
    };
  }

  if (state.markersPool <= 0) {
    return resolveMarkerExhaustion(baseState);
  }

  return {
    ...baseState,
    phase: "PLAYING",
    currentPlayer: getOpponent(action.player),
    turn: state.turn + 1,
    lastMover: null,
  };
}
